import React, { useMemo, useCallback, useEffect, useState } from 'react';
import { Command } from 'cmdk';
import { useCommandPaletteContext } from '../CommandPaletteContext';
import { useDebouncedSearch } from '../hooks';
import { CommandItem } from './CommandItem';
import { CommandGroup } from './CommandGroup';
// import { filterAndScoreCommands } from '../utils/scoring';
import { filterAndScoreCommandsFuse } from '../utils/scoringFuse';
import type { Command as CommandType } from '../types';
import styles from '../CommandPalette.module.scss';
import { cn } from '@/lib/utils';
import { useScoringDebug } from '../ScoringDebugContext';

type ScoredCommand = { command: CommandType; score: number };

function groupScored(scored: ScoredCommand[]): Record<string, ScoredCommand[]> {
  const groups: Record<string, ScoredCommand[]> = {};
  for (const item of scored) {
    const cat = item.command.category || 'Other';
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(item);
  }
  return groups;
}

export interface CommandListProps {
  /**
   * The commands to display. The list will be filtered and scored
   * against `searchValue` from context automatically.
   */
  commands: CommandType[];
  /**
   * Message shown when the filtered list is empty.
   * @default 'No commands found.'
   */
  emptyMessage?: string;
  /**
   * When provided, filtering is debounced by this many milliseconds instead
   * of reacting to every keystroke immediately.
   */
  delay?: number;
  /**
   * When true, commands are grouped by category with headings.
   * When false (default), all results are rendered as a flat list.
   */
  grouped?: boolean;
}

/**
 * CommandList - Shared, reusable scored & grouped command list.
 *
 * Reads `searchValue` from context and applies the standard scoring /
 * grouping pipeline to the supplied `commands`. Renders a `Command.List`
 * (cmdk) and must therefore be used inside a `Command` (cmdk) element.
 *
 * Plugin page components should use this whenever they want the same
 * filtering UX as the default global list:
 *
 * ```tsx
 * const MyPage: React.FC = () => {
 *   const commands = useMemo(() => [...], []);
 *   return <CommandList commands={commands} />;
 * };
 * ```
 *
 * Pages that need fully custom rendering can skip this component and
 * render their own `Command.List` or other content directly.
 */
export const CommandList: React.FC<CommandListProps> = ({
  commands,
  emptyMessage = 'No commands found.',
  delay,
  grouped = false,
}) => {
  const { searchValue, addRecent, setLoading } = useCommandPaletteContext();
  const debug = useScoringDebug();
  const debouncedSearch = useDebouncedSearch(delay ?? 0);
  const effectiveSearch = delay !== undefined ? debouncedSearch : searchValue;

  const [scored, setScored] = useState<ScoredCommand[]>([]);

  useEffect(() => {
    let cancelled = false;
    setLoading('scoring', true);
    Promise.resolve(
      debug.enabled && debug.engine === 'fuse'
        ? filterAndScoreCommandsFuse(commands, effectiveSearch, 1, {
          threshold: debug.fuseThreshold,
          ignoreLocation: debug.fuseIgnoreLocation,
        })
        : filterAndScoreCommandsFuse(commands, effectiveSearch),
    ).then((result) => {
      if (!cancelled) {
        setScored(result);
        setLoading('scoring', false);
      }
    });
    return () => {
      cancelled = true;
      setLoading('scoring', false);
    };
  }, [commands, effectiveSearch, debug.enabled, debug.engine, debug.fuseThreshold, debug.fuseIgnoreLocation]);

  const filteredGroups = useMemo(
    () => grouped ? groupScored(scored) : null,
    [grouped, scored],
  );

  const handleSelect = useCallback((command: CommandType) => {
    addRecent(command);
    command.primary.action();
  }, [addRecent]);

  // console.log('[CommandList] Rendered:', { searchValue: effectiveSearch, commands, filteredGroups });

  return (
    <Command.List className={cn(styles.commandList, "scrollable")}>
      <Command.Empty>{emptyMessage}</Command.Empty>
      {filteredGroups
        ? Object.entries(filteredGroups).map(([category, items]) => (
          <CommandGroup key={category} heading={category}>
            {items.map(({ command: cmd, score }) => (
              <CommandItem
                key={cmd.id}
                command={cmd}
                onSelect={() => handleSelect(cmd)}
                score={debug.enabled && debug.showScores ? score : undefined}
              />
            ))}
          </CommandGroup>
        ))
        : scored.map(({ command: cmd, score }) => (
          <CommandItem
            key={cmd.id}
            command={cmd}
            onSelect={() => handleSelect(cmd)}
            score={debug.enabled && debug.showScores ? score : undefined}
          />
        ))
      }
    </Command.List>
  );
};
